/**
 * Deakin Life Funnelback configuration.
 * Aligned with live search package + results page on dxp-au-admin.
 */
/** Public search API host (no admin SAML login). Dashboard: dxp-au-admin.funnelback.squiz.cloud */
export const DEAKIN_FUNNELBACK_HOST =
  'https://dxp-au-search.funnelback.squiz.cloud';

export const DEAKIN_FUNNELBACK_COLLECTION = 'deakin~sp-deakinlife-news-collection';

export const DEAKIN_FUNNELBACK_PROFILE = 'deakin-life-news-results_preview';

export const DEAKIN_SEARCH_PAGE_SIZE = 12;

/** Live Funnelback date facet (metadata class `d`, dynamic date ranges). */
export const DEAKIN_DATE_FACET_ID = 'Date|d';

/** Funnelback facet ids (Name|metadataClass) for Topics and Faculty filters. */
export const DEAKIN_TOPICS_FACET_ID = 'Topics|topics';
export const DEAKIN_FACULTY_FACET_ID = 'Faculty|faculty';

/** Indexed in Funnelback but not shown as a search filter in the UI. */
export const DEAKIN_TAGS_FACET_ID = 'Tags|tags';

export const DEAKIN_FUNNELBACK_SEARCH_CONNECTOR =
  'https://TODO.deakin.edu.au/_design/connectors/funnelback-search';

export const DEAKIN_FUNNELBACK_AUTOCOMPLETE_CONNECTOR =
  'https://TODO.deakin.edu.au/_design/connectors/funnelback-autocomplete';

/**
 * Funnelback listMetadata field names (index classes from metadata mappings).
 */
export const DEAKIN_RESULT_META = {
  title: ['t', 'title'],
  summary: ['c', 'summary', 'description'],
  publishedDate: ['publishedDate', 'dxp-publishedDate'],
  topic: ['topics', 'dxp-topic'],
  faculty: ['faculty', 'dxp-faculty'],
  image: ['articleImage', 'thumbnailUrl', 'image', 'thumbnail'],
  url: ['liveUrl', 'url'],
} as const;

/** Human labels for approved Explore topic keys (facet + result cards). */
export const DEAKIN_TOPIC_LABELS: Record<string, string> = {
  'accommodation-and-transport': 'Accommodation and transport',
  'assessment-and-study-support': 'Assessment and study support',
  'careers-and-employability': 'Careers and employability',
  'diversity-equity-and-inclusion': 'Diversity, equity and inclusion',
  'get-involved': 'Get involved',
  'health-and-wellbeing': 'Health and wellbeing',
  'international-students': 'International students',
  'it-essentials': 'IT essentials',
  'sport-and-recreation': 'Sport and recreation',
  'student-stories': 'Student stories',
  'study-abroad': 'Study abroad',
};

/** Human labels for approved faculty keys. */
export const DEAKIN_FACULTY_LABELS: Record<string, string> = {
  'faculty-of-arts-and-education': 'Faculty of Arts and Education',
  'faculty-of-business-and-law': 'Faculty of Business and Law',
  'faculty-of-health': 'Faculty of Health',
  'faculty-of-science-engineering-and-built-environment':
    'Faculty of Science, Engineering and Built Environment',
};

/** Normalise Funnelback facet/metadata text to slug keys used in label maps. */
export function facetValueToSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/;/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function deakinTopicLabel(topicKey: string): string {
  const trimmed = topicKey.trim();
  if (!trimmed) {
    return '';
  }
  return (
    DEAKIN_TOPIC_LABELS[trimmed] ??
    DEAKIN_TOPIC_LABELS[facetValueToSlug(trimmed)] ??
    trimmed
  );
}

export function deakinFacultyLabel(facultyKey: string): string {
  const trimmed = facultyKey.trim();
  if (!trimmed) {
    return '';
  }
  return (
    DEAKIN_FACULTY_LABELS[trimmed] ??
    DEAKIN_FACULTY_LABELS[facetValueToSlug(trimmed)] ??
    trimmed
  );
}

function resolveLabelMapKey(
  labels: Record<string, string>,
  raw: string,
): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  const slug = facetValueToSlug(trimmed);
  if (labels[slug]) {
    return slug;
  }
  if (labels[trimmed]) {
    return trimmed;
  }
  const lower = trimmed.toLowerCase();
  for (const [key, label] of Object.entries(labels)) {
    if (label.toLowerCase() === lower) {
      return key;
    }
    if (facetValueToSlug(label) === slug) {
      return key;
    }
  }
  return slug;
}

/** Map page metadata topic text (label or slug) to Funnelback facet value. */
export function resolveTopicFacetValue(raw: string): string {
  return resolveLabelMapKey(DEAKIN_TOPIC_LABELS, raw);
}

/** Map page metadata faculty text (label or slug) to Funnelback facet value. */
export function resolveFacultyFacetValue(raw: string): string {
  return resolveLabelMapKey(DEAKIN_FACULTY_LABELS, raw);
}
